/**
 * `storeQuotes.*` — Store Admin quotes (Commerce, M4).
 *
 * A quote is a priced line sheet sent to a buyer before they commit, with an
 * expiry date. Prices are *captured* at creation, so a later catalogue change
 * never rewrites what a buyer was already quoted.
 *
 * **Status vs phase.** The stored column (migration 0134) only ever holds
 * `draft · sent · converted`. What an operator reads is the `phase`, which folds
 * the expiry window in: `expiring` and `expired` are `sent` rows either side of
 * a 7-day cutoff, derived on every read and never persisted. The enum's fourth
 * value, `expired`, is deliberately never written — storing it would need a
 * nightly sweep and would be wrong until that sweep ran.
 *
 * **Not built here.** Conversion to an order, the PDF, and any actual send.
 * `sent` means the quote is outstanding with the buyer, not that an email left.
 *
 * @see packages/domain/src/commerce/store · docs/spec/23-store-quotes.md
 */
import { z } from "zod";
import { uuid, paged, moneyPaise, isoDate } from "../_common.js";
import { orderKind } from "./store-orders.js";

export const quoteStatus = z.enum(["draft", "sent", "converted", "expired"]);
export type QuoteStatus = z.infer<typeof quoteStatus>;

/** What the badge actually says. Derived — see the module docblock. */
export const quotePhase = z.enum(["draft", "pending", "expiring", "expired", "converted"]);
export type QuotePhase = z.infer<typeof quotePhase>;

export const quoteRow = z.object({
  quoteId: uuid,
  /** Allocated on send; empty string while the quote is still a draft. */
  docNumber: z.string(),
  status: quoteStatus,
  phase: quotePhase,
  customerId: uuid,
  customerName: z.string(),
  customerGstin: z.string().nullable(),
  /** Units across every line. Summed from the lines, never stored. */
  qty: z.number().int().nonnegative(),
  /** `yyyy-mm-dd` — a DATE column, deliberately not a timestamp. */
  expiresOn: z.string().nullable(),
  // No `discountPaise`: unlike an order, the quote header carries no discount
  // column. Per-line `discountBps` is the only discount a quote has.
  subtotalPaise: moneyPaise,
  taxPaise: moneyPaise,
  totalPaise: moneyPaise,
  coverMessage: z.string().nullable(),
  sentAt: z.string().datetime().nullable(),
  /** The order this quote was converted into, once it has been. */
  convertedOrderId: uuid.nullable(),
  /** Spec 16 R1 — `at_once` for everything raised before pre-book existed. */
  kind: orderKind,
  /** The season booked — pre-book only. */
  sourceCollectionId: uuid.nullable(),
  sourceCollectionName: z.string().nullable(),
  /**
   * Lets the Season link render its "(archived)" suffix without a second query.
   * Admin surfaces never hide an archived collection (spec 15 R3).
   */
  sourceCollectionStatus: z.enum(["active", "archived"]).nullable(),
  /** The promised delivery window, `yyyy-mm-dd` — pre-book only. */
  deliveryStartsOn: z.string().nullable(),
  deliveryEndsOn: z.string().nullable(),
  /** Who raised it — the list's "Rep" column. */
  repName: z.string().nullable(),
  createdAt: z.string().datetime(),
});

export const quoteLine = z.object({
  id: uuid,
  position: z.number().int(),
  /** The item's name, or the free-text description for a line whose item is gone. */
  name: z.string(),
  sku: z.string().nullable(),
  /** Number of packs (spec 13, R7). */
  qty: z.number().int(),
  /** The pack this line was quoted against; null for legacy piece-era lines. */
  packName: z.string().nullable(),
  /** Pieces in one pack — `qty × packPieces` is the pieces shown. */
  packPieces: z.number().int().nullable(),
  capturedPricePaise: moneyPaise,
  discountBps: z.number().int(),
  taxBps: z.number().int(),
  lineTotalPaise: moneyPaise,
});

// ── list ──────────────────────────────────────────────────────────
export const listInput = z.object({
  cursor: z.string().optional(),
  limit: z.number().int().min(1).max(100).default(50),
  q: z.string().optional(),
  /** Omitted = every status (the "All" tab). */
  status: quoteStatus.optional(),
  /**
   * Filter by derived phase — how the Pending / Expiring soon / Expired tabs
   * work. Applied after the page is read, because expiry isn't in the table.
   */
  phase: quotePhase.optional(),
});
export const listOutput = paged(quoteRow);

// ── counts (tab badges + the page subtitle) ───────────────────────
export const countsOutput = z.object({
  all: z.number().int().nonnegative(),
  draft: z.number().int().nonnegative(),
  sent: z.number().int().nonnegative(),
  converted: z.number().int().nonnegative(),
  /** Derived phases — sent rows split by the expiry window. */
  pending: z.number().int().nonnegative(),
  expiring: z.number().int().nonnegative(),
  expired: z.number().int().nonnegative(),
  /** Value of the quotes still live — the subtitle's "open". */
  openPaise: moneyPaise,
});

// ── get ───────────────────────────────────────────────────────────
export const getInput = z.object({ quoteId: uuid });
export const getOutput = quoteRow.extend({ lines: z.array(quoteLine) });

// ── create ────────────────────────────────────────────────────────
export const createLine = z.object({
  listingId: uuid,
  /** Number of **packs** (spec 13 R7), not pieces. */
  qty: z.number().int().min(1),
  /**
   * The library pack being quoted. Omitted resolves to the listing's primary
   * pricing entry server-side — optional so a client that only knows
   * `{listingId, qty}` keeps working, and because a single-entry listing has
   * nothing to choose between.
   */
  packId: uuid.optional(),
});
export const createInput = z
  .object({
    customerId: uuid,
    /** `yyyy-mm-dd`. Optional — a quote with no expiry never lapses. */
    expiresOn: isoDate.nullable().optional(),
    coverMessage: z.string().trim().max(2000).nullable().optional(),
    lines: z.array(createLine).min(1, "Add at least one product."),
    /** Spec 16 R1. Defaults to the only kind that existed before pre-book. */
    kind: orderKind.default("at_once"),
    /** The season being booked — pre-book only (R2/R3). */
    sourceCollectionId: uuid.nullable().optional(),
    /** The promised delivery window — pre-book only, both or neither (R2). */
    deliveryStartsOn: isoDate.nullable().optional(),
    deliveryEndsOn: isoDate.nullable().optional(),
  })
  .superRefine((v, ctx) => {
    // Only the pre-book branch is validated here. An at-once quote is
    // *normalised* server-side (stale hidden values are nulled, not rejected),
    // so rejecting them at the boundary would break the form's "keep values
    // across a kind toggle" behaviour for no gain.
    if (v.kind !== "prebook") return;
    if (!v.sourceCollectionId) {
      ctx.addIssue({
        code: "custom",
        path: ["sourceCollectionId"],
        message: "Pick the season being booked",
      });
    }
    if (!v.deliveryStartsOn || !v.deliveryEndsOn) {
      // On BOTH blank paths: a single message on `start` leaves the end field
      // looking fine while the form refuses to submit.
      const message = "Pre-book needs a delivery window";
      if (!v.deliveryStartsOn) ctx.addIssue({ code: "custom", path: ["deliveryStartsOn"], message });
      if (!v.deliveryEndsOn) ctx.addIssue({ code: "custom", path: ["deliveryEndsOn"], message });
    } else if (v.deliveryEndsOn < v.deliveryStartsOn) {
      ctx.addIssue({
        code: "custom",
        path: ["deliveryEndsOn"],
        message: "End date is before start",
      });
    }
  });
export const createOutput = z.object({ quoteId: uuid });

// ── transition ────────────────────────────────────────────────────
export const transitionInput = z.object({
  quoteId: uuid,
  status: quoteStatus,
  reason: z.string().trim().max(500).optional(),
});
export const transitionOutput = z.object({ quoteId: uuid, status: quoteStatus });

// ── convert (spec 16 R6) ──────────────────────────────────────────
export const convertInput = z.object({
  quoteId: uuid,
  /** Ship-to, captured here — a quote carries none. */
  shipToText: z.string().trim().max(2000).nullable().optional(),
  /**
   * At-once only. A pre-book order's promised date derives from the end of its
   * delivery window (R5), so this is ignored for one.
   */
  dispatchBy: isoDate.nullable().optional(),
});
export const convertOutput = z.object({
  quoteId: uuid,
  orderId: uuid,
  /** `false` when the quote had already been converted — the call is idempotent. */
  created: z.boolean(),
});

export type ListQuotesInput = z.infer<typeof listInput>;
export type CreateQuoteInput = z.infer<typeof createInput>;
export type TransitionQuoteInput = z.infer<typeof transitionInput>;
